import React from 'react';
import classnames from 'classnames';
import listIcon from '../../icons/list-ul.svg';
import toReviewIcon from '../../icons/toReview-icon.svg';
import { PermissionFlag } from 'v2/context/permissionFlagContext';
import Count from 'v2/common/Count/Count';
import { BsArchive } from 'react-icons/bs';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { GenericObject, Permission, ReviewTabEnum } from 'v2/models';
import styles from './searchTabs.module.scss';
import { PERMISSION_NAME } from 'v2/utils/permissions';
import { searchViewPath } from 'v2/utils/searchViewUtils';
import useGetBadgeCount from 'v2/queries/useGetBadgeCount';
import { useArchivedSearchCount } from 'v2/queries/searches/useArchivedSearchCount';
import { useInReviewSearchCount } from 'v2/queries/searches/useInReviewSearchCount';
import { useMasterOrganization } from 'v2/queries';
import { useFreeUser } from 'v2/hooks/useFreeUser';

interface SearchTabsStateProps {
  permissions: {
    permissions: { [key: string]: Permission };
    loaded: boolean;
  };
}

interface tabButtonParameters {
  id: string;
  route: string;
  subRoute?: string;
  icon?: string;
  btnText: string;
  renderWithEmptyList?: boolean;
  allowPlural?: boolean;
  searchListLength?: number;
  allowCounter?: boolean;
  counterValue?: number;
  allowedPermissions: string[];
}

const SearchTabs = ({ permissions }: SearchTabsStateProps) => {
  const { data: masterOrganization } = useMasterOrganization();
  const [isFreeUser] = useFreeUser();
  const history = useHistory();
  const location = useLocation();

  const { data: badges } = useGetBadgeCount();

  const {
    data: in_review_searches_count = 0,
    isFetched: isReviewCountFetched,
  } = useInReviewSearchCount();
  const {
    data: archived_searches_count = 0,
    isFetched: isArchivedSearchCountFetched,
  } = useArchivedSearchCount();
  const searches_loaded = isReviewCountFetched && isArchivedSearchCountFetched;

  const getBadgeCount = (
    searchType: 'active' | 'inReview' | 'forReview' | 'inProgress'
  ) => {
    return badges?.[searchType]?.count;
  };

  const changeRoute = (path: string) => {
    history.push(path);
  };

  const renderTabButton = ({
    id,
    route,
    subRoute = '',
    icon = listIcon,
    btnText,
    renderWithEmptyList = true,
    searchListLength = 0,
    allowCounter = false,
    counterValue = 0,
    allowedPermissions,
  }: tabButtonParameters) => {
    if (!renderWithEmptyList && !searchListLength) {
      return null;
    }
    const fullRoute = route + subRoute;
    return (
      <React.Fragment key={id}>
        <PermissionFlag
          permissions={permissions.permissions}
          namePermission={allowedPermissions}
          considerSubscriptionActive={true}
        >
          <span
            id={id}
            aria-current={'page'}
            className={classnames(
              'subNavLink',
              location.pathname.includes(route) ? 'active' : '',
              styles.linkButtonSearches
            )}
            data-cy={`requests:requests-tab-${id}`}
            onClick={() => changeRoute(fullRoute + '/1')}
          >
            <span className="ml-lg-5 d-flex align-items-center badge badge-pill text-primary">
              {id === 'archivedButton' ? (
                <BsArchive className="mr-2" size={25} />
              ) : (
                <img
                  src={icon}
                  className={classnames('mr-2', styles.iconSearches)}
                  alt={'navIcon'}
                />
              )}
              {btnText}
              {allowCounter ? (
                <Count className={styles.topCount} value={counterValue} />
              ) : null}
            </span>
          </span>
        </PermissionFlag>
      </React.Fragment>
    );
  };

  const tabs = [
    {
      path: '/requests/requester/',
      renders: [
        renderTabButton({
          id: 'activeButton',
          route: searchViewPath.active,
          btnText: 'Active Requests',
          allowCounter: true,
          counterValue: getBadgeCount('active'),
          allowedPermissions: [
            PERMISSION_NAME.CREATE_SEARCH,
            PERMISSION_NAME.CREATE_SEARCH_OWN,
          ],
        }),
        renderTabButton({
          id: 'inProgress',
          route: searchViewPath.inProgress,
          btnText: 'In Progress',
          allowCounter: true,
          counterValue: getBadgeCount('inProgress'),
          allowedPermissions: [
            PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
            PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
          ],
        }),
        renderTabButton({
          id: 'inReview',
          route: searchViewPath.inReview,
          icon: toReviewIcon,
          btnText: 'In Review',
          renderWithEmptyList: false,
          allowCounter: true,
          counterValue: getBadgeCount('inReview'),
          searchListLength: in_review_searches_count,
          allowedPermissions: [
            PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
            PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
          ],
        }),
        renderTabButton({
          id: searchViewPath.archived,
          route: searchViewPath.archived,
          btnText: 'Archived Requests',
          renderWithEmptyList: false,
          searchListLength: archived_searches_count,
          allowedPermissions: [
            PERMISSION_NAME.CREATE_SEARCH,
            PERMISSION_NAME.CREATE_SEARCH_OWN,
            PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
            PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
          ],
        }),
      ],
    },
    {
      path: '/requests/reviewer/',
      renders: [
        renderTabButton({
          id: 'forReview',
          route: searchViewPath.forReview,
          subRoute: `/${ReviewTabEnum.needs_review}`,
          icon: toReviewIcon,
          btnText: 'Review',
          allowCounter: true,
          counterValue: getBadgeCount('forReview'),
          allowedPermissions: [
            PERMISSION_NAME.REVIEW_SEARCH_ORG,
            PERMISSION_NAME.REVIEW_SEARCH_MASTER_ORG,
          ],
        }),
        renderTabButton({
          id: 'allButton',
          route: searchViewPath.allSearches,
          btnText: 'All Time',
          allowedPermissions: [
            PERMISSION_NAME.REVIEW_SEARCH_ORG,
            PERMISSION_NAME.REVIEW_SEARCH_MASTER_ORG,
          ],
        }),
      ],
    },
  ];

  if (!searches_loaded) {
    return null;
  }

  if (isFreeUser && masterOrganization?.unqualified) {
    return null;
  }

  return (
    <>
      {tabs.map((tab) =>
        location.pathname.includes(tab.path) ? tab.renders : null
      )}
    </>
  );
};

const mapStateToProps = (state: GenericObject): SearchTabsStateProps => {
  return {
    permissions: state.permissions,
  };
};

export default connect<SearchTabsStateProps>(mapStateToProps)(SearchTabs);
